import type { FC } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles/createTheme";
import Menu01Icon from "@untitled-ui/icons-react/build/esm/Menu01";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DashboardIcon from "@mui/icons-material/Dashboard";

import { AccountButton } from "../account-button";

import { paths } from "src/paths";

const TOP_NAV_HEIGHT: number = 64;
const SIDE_NAV_WIDTH: number = 280;

interface TopNavProps {
  onMobileNavOpen?: () => void;
  admin: boolean;
}

export const TopNav: FC<TopNavProps> = (props) => {
  const { onMobileNavOpen, admin } = props;
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();

  // Hide switcher during onboarding
  const isOnboarding = location.pathname.startsWith("/onboarding");

  const isHomePage =
    location.pathname === "/" ||
    location.pathname.includes("/wills-and-trusts") ||
    location.pathname.includes("/health-care-directive") ||
    location.pathname.includes("/financial-power-of-attorney") ||
    location.pathname.includes("/account");

  // Check if user has premium access to show dashboard toggle
  // const subscriptionStatus =
  //   selectedHousehold?.head_of_household?.total_payment_tier
  //     ?.subscription_status;
  // const subscriptionStatus = 'blocked';
  // const hasPremiumAccess =
  //   subscriptionStatus === "legacy" ||
  //   (subscriptionStatus &&
  //     ["active", "trialing", "inactive", "expired"].includes(
  //       subscriptionStatus
  //     ));
  // const hasPremiumAccess = false;

  return (
    <Box
      component="header"
      sx={{
        backdropFilter: isHomePage ? "none" : "blur(6px)",
        backgroundColor: "#fcfcfc",
        position: "sticky",
        left: {
          md: `${SIDE_NAV_WIDTH}px`,
        },
        top: admin && lgUp ? "48px" : "0px",
        width: {
          md: `calc(100% - ${SIDE_NAV_WIDTH}px)`,
        },
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{
          minHeight: TOP_NAV_HEIGHT,
          px: 2,
        }}
      >
        <Stack alignItems="center" direction="row" spacing={2}>
          {!lgUp && (
            <IconButton onClick={onMobileNavOpen}>
              <SvgIcon>
                <Menu01Icon />
              </SvgIcon>
            </IconButton>
          )}
        </Stack>
        <Stack alignItems="center" direction="row" spacing={2}>
          {!isOnboarding && (
            <Box
              sx={{
                bgcolor: '#EBEBEB',
                borderRadius: '12px',
                p: '3px',
                pr: '5px',
                display: 'flex',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '3px',
                  left: '3px',
                  width: '32px',
                  height: '32px',
                  bgcolor: 'white',
                  borderRadius: '9px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: 'translateX(34px)',
                }}
              />
              <Tooltip title="Checklist">
                <Button
                  size="small"
                  onClick={() => {
                    sessionStorage.setItem('totalViewPreference', 'express');
                    navigate(paths.dashboard.express);
                  }}
                  sx={{
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    p: 0,
                    borderRadius: '10px',
                    color: 'text.secondary',
                    bgcolor: 'transparent',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'color 0.2s',
                    '&:hover': {
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  <ChecklistIcon fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title="Dashboard">
                <Button
                  size="small"
                  onClick={() => {
                    sessionStorage.setItem('totalViewPreference', 'dashboard');
                    navigate(paths.dashboard.index);
                  }}
                  sx={{
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    p: 0,
                    borderRadius: '10px',
                    color: 'text.primary',
                    bgcolor: 'transparent',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'color 0.2s',
                    '&:hover': {
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  <DashboardIcon fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
          )}
          <AccountButton />
        </Stack>
      </Stack>
    </Box>
  );
};

TopNav.propTypes = {
  onMobileNavOpen: PropTypes.func,
};
